import { Injectable, inject } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ErrorMessageService {
    private readonly translateService = inject(TranslateService);

    async resolveApiError(error: unknown, fallbackApiKey: string = 'api.err.default'): Promise<string> {
        const apiTranslationKey = this.resolveApiTranslationKey(error);
        if (apiTranslationKey) {
            const translated = await this.translateApiKey(apiTranslationKey);
            if (translated) {
                return translated;
            }
        }

        const fallbackTranslation = fallbackApiKey.startsWith('api.')
            ? await this.translateApiKey(fallbackApiKey)
            : await this.translateIfExists(fallbackApiKey);
        if (fallbackTranslation) {
            return fallbackTranslation;
        }

        const defaultTranslation = await this.translateApiKey('api.err.default');
        return defaultTranslation ?? 'api.err.default';
    }

    async resolveSpaError(spaKey: string = 'unknown'): Promise<string> {
        const normalizedKey = this.normalizeSpaKey(spaKey);
        const translated = await this.translate(normalizedKey);

        if (translated !== normalizedKey || normalizedKey === 'err.spa.unknown') {
            return translated;
        }

        return this.translate('err.spa.unknown');
    }

    private resolveApiTranslationKey(error: unknown): string | null {
        const errorCode = this.extractApiErrorCode(error);
        return errorCode ? this.normalizeApiKey(errorCode) : null;
    }

    private extractApiErrorCode(error: unknown): string | null {
        const candidatePaths: readonly (readonly string[])[] = [
            ['body', 'code'],
            ['code'],
            ['error', 'body', 'code'],
            ['error', 'code'],
            ['body', 'client', 'code'],
            ['client', 'code'],
        ];

        for (const path of candidatePaths) {
            const value = this.readString(error, path);
            if (value && this.isSupportedApiErrorCode(value)) {
                return value;
            }
        }

        return null;
    }

    private normalizeSpaKey(spaKey: string): string {
        return spaKey.startsWith('err.spa.') ? spaKey : `err.spa.${spaKey}`;
    }

    private normalizeApiKey(errorCode: string): string {
        return errorCode.startsWith('api.') ? errorCode : `api.${errorCode}`;
    }

    private async translateApiKey(key: string): Promise<string | null> {
        const translated = await this.translateIfExists(key);
        if (translated) {
            return translated;
        }

        const apiTranslations = await firstValueFrom(this.translateService.get('api'));
        if (!this.isRecord(apiTranslations)) {
            return null;
        }

        const apiKey = key.startsWith('api.') ? key.slice(4) : key;
        const literalTranslation = this.readApiTranslation(apiTranslations, apiKey);
        if (literalTranslation) {
            return literalTranslation;
        }

        const nestedTranslation = this.readApiTranslation(apiTranslations, apiKey.split('.'));
        return nestedTranslation;
    }

    private async translateIfExists(key: string): Promise<string | null> {
        const translated = await firstValueFrom(this.translateService.get(key));
        return translated !== key ? translated : null;
    }

    private async translate(key: string): Promise<string> {
        return firstValueFrom(this.translateService.get(key));
    }

    private readString(source: unknown, path: readonly string[]): string | null {
        let current: unknown = source;

        for (const segment of path) {
            if (!this.isRecord(current) || !(segment in current)) {
                return null;
            }

            current = current[segment];
        }

        return typeof current === 'string' ? current : null;
    }

    private readApiTranslation(source: Record<string, unknown>, keyOrPath: string | readonly string[]): string | null {
        if (typeof keyOrPath === 'string' && keyOrPath in source) {
            const value = source[keyOrPath];
            return typeof value === 'string' ? value : null;
        }

        const path = typeof keyOrPath === 'string' ? keyOrPath.split('.') : keyOrPath;
        return this.readString(source, path);
    }

    private isSupportedApiErrorCode(value: string): boolean {
        return value.startsWith('err.auth.')
            || value.startsWith('api.err.auth.')
            || value.startsWith('err.identity.')
            || value.startsWith('api.err.identity.');
    }

    private isRecord(value: unknown): value is Record<string, unknown> {
        return typeof value === 'object' && value !== null;
    }
}
